import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import ColegioService from '../services/ColegioService';
import { useAuth } from '../contexts/AuthContext';
import cursoService from '../services/CursoService';
import aulaService from '../services/AulaService';
import ActiveColegioBadge from './common/ActiveColegioBadge';
import SolicitudesDropdown from './common/SolicitudesDropdown';
import LoadingSpinner from './common/LoadingSpinner';
import { hasOpenAIChatsEnabled, isFeatureEnabled } from '../utils/adminMenu';
import ProfessorMenu from './common/menus/ProfessorMenu';
import SearchableSelect from './common/SearchableSelect';

const hasModuloAlumnosEnabled = (flags) =>
  isFeatureEnabled(flags, 'ModuloAlumnos');

const hasAgentesClasicosEnabled = (flags) =>
  isFeatureEnabled(flags, 'PermiteAgentesClasicosGPT');

const normalizeListResponse = (response) => (
  Array.isArray(response?.items) ? response.items : Array.isArray(response) ? response : []
);

const normalizeIdList = (value) => (
  Array.isArray(value)
    ? value.map((item) => (item == null ? '' : String(item))).filter(Boolean)
    : []
);

const formatCourseLabel = (curso) => (
  curso?.nombre
  || curso?.Nombre
  || `${curso?.valorAnio || curso?.ValorAnio || '—'}° ${curso?.division || curso?.Division || '—'} - ${curso?.cicloLectivo || curso?.CicloLectivo || '—'}`
);

const getMateriaId = (materia) => String(materia?.id || materia?.Id || '');

const getMateriaCourseIds = (materia) => normalizeIdList(materia?.cursoIds || materia?.CursoIds);

const formatMateriaLabel = (materia) => {
  const nombre = materia?.nombre || materia?.Nombre || 'Materia';
  const codigo = materia?.codigo || materia?.Codigo || '';
  return codigo ? `${nombre} · ${codigo}` : nombre;
};

const ProfesorCreateAula = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const activeColegio = useMemo(() => ColegioService.getActiveColegio(), []);
  const activeColegioId = useMemo(() => ColegioService.getActiveColegioId(), []);
  const activeColegioName = activeColegio?.nombre || activeColegio?.name || activeColegio?.descripcion || 'Colegio';
  const featureFlags = useMemo(() => ColegioService.getFeatureFlags(), []);
  const hasOpenAIChats = useMemo(() => hasOpenAIChatsEnabled(featureFlags), [featureFlags]);
  const hasModuloAlumnos = useMemo(() => hasModuloAlumnosEnabled(featureFlags), [featureFlags]);
  const hasAgentesClasicos = useMemo(() => hasAgentesClasicosEnabled(featureFlags), [featureFlags]);
  const canCreateAula = Boolean(user?.permiteCrearAula);

  const [cursos, setCursos] = useState([]);
  const [materias, setMaterias] = useState([]);
  const [classicAssistants, setClassicAssistants] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoadingMaterias, setIsLoadingMaterias] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const currentYear = new Date().getFullYear();
  const [form, setForm] = useState({
    nombre: '',
    descripcion: '',
    codigoSuscripcion: '',
    cursoIds: [],
    materiaIds: [],
    idsAsistentes: [],
    aTodosMisCursos: false,
    aTodasMisMaterias: false,
    cicloLectivo: currentYear,
    idColegio: activeColegioId || ''
  });

  useEffect(() => {
    if (!activeColegioId || !hasModuloAlumnos) return;

    const fetchData = async () => {
      setIsLoadingData(true);
      try {
        const requests = [
          cursoService.getMisCursosProfesor({ idColegio: activeColegioId })
        ];

        if (hasAgentesClasicos) {
          requests.push(aulaService.getMisAsistentesGptClasicosParaAula({ idColegio: activeColegioId }));
        }

        const [cursosRes, assistantsRes = []] = await Promise.all(requests);
        setCursos(normalizeListResponse(cursosRes));
        setClassicAssistants(Array.isArray(assistantsRes) ? assistantsRes : []);
      } catch (error) {
        setMessage(error.message || 'No pudimos cargar los datos del aula.');
        setMessageType('error');
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchData();
  }, [activeColegioId, hasAgentesClasicos, hasModuloAlumnos]);

  const selectedCycle = Number(form.cicloLectivo) || currentYear;

  const availableCourses = useMemo(() => (
    cursos
      .filter((curso) => Number(curso?.cicloLectivo || curso?.CicloLectivo || 0) === selectedCycle)
      .sort((a, b) => formatCourseLabel(a).localeCompare(formatCourseLabel(b), 'es', { sensitivity: 'base' }))
  ), [cursos, selectedCycle]);

  const availableCourseIds = useMemo(() => (
    availableCourses
      .map((curso) => String(curso?.id || curso?.Id || ''))
      .filter(Boolean)
  ), [availableCourses]);

  const selectedCourseIdsKey = useMemo(() => (
    [...form.cursoIds].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })).join('|')
  ), [form.cursoIds]);

  useEffect(() => {
    if (!activeColegioId || !hasModuloAlumnos) return;

    if (form.cursoIds.length === 0) {
      setMaterias([]);
      setForm((prev) => (
        prev.materiaIds.length === 0 && !prev.aTodasMisMaterias
          ? prev
          : { ...prev, materiaIds: [], aTodasMisMaterias: false }
      ));
      return;
    }

    let cancelled = false;

    const fetchMaterias = async () => {
      setIsLoadingMaterias(true);
      try {
        const list = await aulaService.getMisMateriasSeleccionablesParaAula({
          idColegio: activeColegioId,
          cursoIds: form.cursoIds,
          cicloLectivo: selectedCycle
        });

        if (cancelled) return;

        const items = Array.isArray(list) ? list : [];
        setMaterias(items);

        setForm((prev) => {
          const nextAvailableIds = items
            .map((materia) => getMateriaId(materia))
            .filter(Boolean);
          const nextAvailableSet = new Set(nextAvailableIds);
          const nextMateriaIds = prev.aTodasMisMaterias
            ? nextAvailableIds
            : prev.materiaIds.filter((id) => nextAvailableSet.has(String(id)));

          return {
            ...prev,
            materiaIds: nextMateriaIds,
            aTodasMisMaterias: prev.aTodasMisMaterias && nextAvailableIds.length > 0
          };
        });
      } catch (error) {
        if (cancelled) return;

        setMaterias([]);
        setForm((prev) => ({ ...prev, materiaIds: [], aTodasMisMaterias: false }));
        setMessage(error.message || 'No pudimos cargar las materias disponibles para los cursos elegidos.');
        setMessageType('error');
      } finally {
        if (!cancelled) {
          setIsLoadingMaterias(false);
        }
      }
    };

    fetchMaterias();

    return () => {
      cancelled = true;
    };
  }, [activeColegioId, form.cursoIds, hasModuloAlumnos, selectedCourseIdsKey, selectedCycle]);

  const cursoOptions = useMemo(() => (
    availableCourses
      .map((curso) => {
        const id = String(curso?.id || curso?.Id || '');
        const nombre = formatCourseLabel(curso);
        const cicloLectivo = curso?.cicloLectivo || curso?.CicloLectivo || '';
        const valorAnio = curso?.valorAnio || curso?.ValorAnio || '';
        const division = curso?.division || curso?.Division || '';
        const secondary = [valorAnio ? `${valorAnio}°` : '', division ? `División ${division}` : '', cicloLectivo ? `Ciclo ${cicloLectivo}` : '']
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label: nombre,
          secondary,
          searchText: `${nombre} ${valorAnio} ${division} ${cicloLectivo}`.trim()
        };
      })
      .filter((option) => option.value)
  ), [availableCourses]);

  const availableMateriaIds = useMemo(() => (
    materias
      .map((materia) => getMateriaId(materia))
      .filter(Boolean)
  ), [materias]);

  const materiaOptions = useMemo(() => (
    materias
      .map((materia) => {
        const id = getMateriaId(materia);
        const label = formatMateriaLabel(materia);
        const nombreArea = materia?.nombreArea || materia?.NombreArea || '';
        const cursosMateria = Array.isArray(materia?.cursos || materia?.Cursos)
          ? (materia?.cursos || materia?.Cursos)
          : [];
        const secondary = [nombreArea, cursosMateria.join(' · ')]
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label,
          secondary,
          searchText: `${label} ${nombreArea} ${cursosMateria.join(' ')}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [materias]);

  const assistantOptions = useMemo(() => (
    classicAssistants
      .map((assistant) => {
        const id = String(assistant?.idOpenAIAssistant || assistant?.IdOpenAIAssistant || '');
        const nombreVisible = assistant?.nombreVisible || assistant?.NombreVisible || assistant?.nombre || assistant?.Nombre || 'Asistente IA';
        const nombreMateria = assistant?.nombreMateria || assistant?.NombreMateria || '';
        const nombreArea = assistant?.nombreArea || assistant?.NombreArea || '';
        const esAsignado = !!(assistant?.esAsignado ?? assistant?.EsAsignado);
        const secondary = [nombreMateria, nombreArea, esAsignado ? 'Asignado directo' : 'Por materia']
          .filter(Boolean)
          .join(' · ');

        return {
          value: id,
          label: nombreVisible,
          secondary,
          searchText: `${nombreVisible} ${nombreMateria} ${nombreArea}`.trim()
        };
      })
      .filter((option) => option.value)
      .sort((a, b) => a.label.localeCompare(b.label, 'es', { sensitivity: 'base' }))
  ), [classicAssistants]);

  const selectedCourseSet = useMemo(() => new Set(form.cursoIds.map(String)), [form.cursoIds]);
  const selectedMateriaSet = useMemo(() => new Set(form.materiaIds.map(String)), [form.materiaIds]);

  const plannedPairs = useMemo(() => {
    const pairs = [];

    materias.forEach((materia) => {
      const materiaId = getMateriaId(materia);
      if (!selectedMateriaSet.has(materiaId)) {
        return;
      }

      getMateriaCourseIds(materia).forEach((courseId) => {
        if (selectedCourseSet.has(courseId)) {
          pairs.push({ courseId, materiaId });
        }
      });
    });

    return pairs;
  }, [materias, selectedCourseSet, selectedMateriaSet]);

  const coursesWithMultipleMaterias = useMemo(() => {
    const counts = new Map();
    plannedPairs.forEach((pair) => {
      counts.set(pair.courseId, (counts.get(pair.courseId) || 0) + 1);
    });

    return availableCourses
      .filter((curso) => counts.get(String(curso?.id || curso?.Id || '')) > 1)
      .map((curso) => formatCourseLabel(curso));
  }, [availableCourses, plannedPairs]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCycleChange = (value) => {
    const nextCycle = Number(value) || currentYear;
    const nextAvailableIds = cursos
      .filter((curso) => Number(curso?.cicloLectivo || curso?.CicloLectivo || 0) === nextCycle)
      .map((curso) => String(curso?.id || curso?.Id || ''))
      .filter(Boolean);
    const nextAvailableSet = new Set(nextAvailableIds);

    setForm((prev) => {
      const nextCursoIds = prev.aTodosMisCursos
        ? nextAvailableIds
        : prev.cursoIds.filter((id) => nextAvailableSet.has(String(id)));

      return {
        ...prev,
        cicloLectivo: value,
        cursoIds: nextCursoIds,
        aTodosMisCursos: prev.aTodosMisCursos && nextAvailableIds.length > 0
      };
    });
  };

  const handleCursosChange = (cursoIds) => {
    const normalizedIds = normalizeIdList(cursoIds);
    setForm((prev) => ({
      ...prev,
      cursoIds: normalizedIds,
      aTodosMisCursos: availableCourseIds.length > 0 && normalizedIds.length === availableCourseIds.length
    }));
  };

  const handleToggleAllCourses = (checked) => {
    if (checked && availableCourseIds.length === 0) {
      setMessage(`No tienes cursos asignados para el ciclo lectivo ${selectedCycle}.`);
      setMessageType('error');
      return;
    }

    setForm((prev) => ({
      ...prev,
      aTodosMisCursos: checked,
      cursoIds: checked ? availableCourseIds : []
    }));
  };

  const handleMateriasChange = (materiaIds) => {
    const normalizedIds = normalizeIdList(materiaIds);
    setForm((prev) => ({
      ...prev,
      materiaIds: normalizedIds,
      aTodasMisMaterias: availableMateriaIds.length > 0 && normalizedIds.length === availableMateriaIds.length
    }));
  };

  const handleToggleAllMaterias = (checked) => {
    if (checked && availableMateriaIds.length === 0) {
      setMessage('No hay materias disponibles para los cursos seleccionados.');
      setMessageType('error');
      return;
    }

    setForm((prev) => ({
      ...prev,
      aTodasMisMaterias: checked,
      materiaIds: checked ? availableMateriaIds : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!canCreateAula) {
      setMessage('No tienes permisos para crear aulas.');
      setMessageType('error');
      return;
    }
    if (!form.nombre.trim()) {
      setMessage('Completa el nombre del aula.');
      setMessageType('error');
      return;
    }
    if (!form.cursoIds.length) {
      setMessage('Selecciona al menos un curso para crear el aula.');
      setMessageType('error');
      return;
    }
    if (!form.materiaIds.length) {
      setMessage('Selecciona al menos una materia para crear el aula.');
      setMessageType('error');
      return;
    }
    if (!plannedPairs.length) {
      setMessage('La combinación actual de cursos y materias no genera aulas válidas.');
      setMessageType('error');
      return;
    }
    if (!form.idColegio) {
      setMessage('No se pudo determinar el colegio activo.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage(plannedPairs.length > 1 ? 'Creando aulas...' : 'Creando aula...');
    setMessageType('info');
    try {
      const payload = {
        nombre: form.nombre,
        descripcion: form.descripcion,
        codigoSuscripcion: '',
        cursoIds: form.cursoIds,
        materiaIds: form.materiaIds,
        idsAsistentes: form.idsAsistentes,
        cicloLectivo: Number(form.cicloLectivo) || currentYear,
        idColegio: form.idColegio,
        ...(form.cursoIds.length === 1 ? { idCurso: form.cursoIds[0] } : {}),
        ...(form.materiaIds.length === 1 ? { idMateria: form.materiaIds[0] } : {})
      };

      await aulaService.createAulaProfesor(payload);
      setMessage(plannedPairs.length > 1 ? 'Aulas creadas correctamente.' : 'Aula creada correctamente.');
      setMessageType('success');
      setTimeout(() => navigate('/my-aulas', { replace: true }), 900);
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el aula.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasModuloAlumnos) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <div>
            <h1 className="dashboard-title">Aulas</h1>
            <p className="dashboard-subtitle">El módulo de alumnos aún no está habilitado para este colegio.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="new-dashboard">
      <aside className="dashboard-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-graduation-cap"></i>
            <span>aluxIA</span>
          </div>
        </div>
        <ProfessorMenu
          activeId="aulas"
          hasOpenAIChats={hasOpenAIChats}
          hasAgentesClasicos={hasAgentesClasicos}
          hasModuloAlumnos={hasModuloAlumnos}
          onNavigate={(item) => item?.path && navigate(item.path)}
        />
        <div className="sidebar-footer">
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Usuario'}</span>
              <span className="user-role">Profesor</span>
            </div>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">Alta de aula</h1>
            <p className="page-subtitle">Crea una o varias aulas para {activeColegioName}.</p>
          </div>
          <div className="header-right">
            <SolicitudesDropdown />
            <ActiveColegioBadge />
            <button className="logout-btn-header" onClick={logout} title="Cerrar Sesión">
              <i className="fas fa-sign-out-alt"></i>
              <span className="logout-text">Salir</span>
            </button>
          </div>
        </header>

        <div className="dashboard-content" style={{ padding: '2rem', maxWidth: '900px' }}>
          <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-door-open" />
                <span>Datos del aula</span>
              </div>
            </div>
            <div className="card-content">
              {!canCreateAula && (
                <p style={{ marginTop: 0, color: '#b91c1c' }}>
                  No tienes permisos para crear aulas. Contacta al administrador.
                </p>
              )}
              <form onSubmit={handleSubmit} className="prof-create-form">
                <div className="form-field">
                  <label>Nombre base</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Aula 1"
                    disabled={!canCreateAula}
                  />
                  <small style={{ color: '#64748b' }}>
                    Si en un mismo curso eliges más de una materia, el sistema agregará el nombre de la materia para diferenciar cada aula.
                  </small>
                </div>
                <div className="form-field">
                  <label>Descripción</label>
                  <textarea
                    value={form.descripcion}
                    onChange={(e) => handleChange('descripcion', e.target.value)}
                    placeholder="Descripción del aula"
                    style={{ borderRadius: '10px', border: '1px solid var(--gray-200)', padding: '0.65rem 0.85rem', fontSize: '0.95rem', minHeight: '80px' }}
                    disabled={!canCreateAula}
                  />
                </div>
                <div className="form-field">
                  <label>Ciclo lectivo</label>
                  <input
                    type="number"
                    value={form.cicloLectivo}
                    onChange={(e) => handleCycleChange(e.target.value)}
                    min="2000"
                    max="2100"
                    disabled={!canCreateAula}
                  />
                </div>
                <div className="form-field">
                  <label>Cursos del aula</label>
                  <SearchableSelect
                    value={form.cursoIds}
                    onChange={handleCursosChange}
                    options={cursoOptions}
                    placeholder="Busca cursos por año, división o nombre"
                    emptyText={`No hay cursos asignados para el ciclo lectivo ${selectedCycle}.`}
                    noResultsText="No encontramos cursos con esa búsqueda."
                    disabled={isLoadingData || !canCreateAula}
                    multiple
                  />
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', fontWeight: 600, color: '#0f172a' }}>
                      <input
                        type="checkbox"
                        checked={form.aTodosMisCursos}
                        onChange={(e) => handleToggleAllCourses(e.target.checked)}
                        disabled={isLoadingData || !canCreateAula}
                      />
                      A todos mis cursos
                    </label>
                    <small style={{ color: '#64748b' }}>
                      Se seleccionarán automáticamente todos tus cursos del ciclo lectivo {selectedCycle}.
                    </small>
                  </div>
                </div>
                <div className="form-field">
                  <label>Materias para los cursos elegidos</label>
                  <SearchableSelect
                    value={form.materiaIds}
                    onChange={handleMateriasChange}
                    options={materiaOptions}
                    placeholder={form.cursoIds.length > 0 ? 'Busca materias por nombre, código, área o curso' : 'Primero selecciona uno o más cursos'}
                    emptyText={form.cursoIds.length > 0 ? 'No hay materias disponibles para esos cursos.' : 'Selecciona cursos para ver las materias.'}
                    noResultsText="No encontramos materias con esa búsqueda."
                    disabled={isLoadingData || isLoadingMaterias || !canCreateAula || form.cursoIds.length === 0}
                    multiple
                  />
                  <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.55rem', fontWeight: 600, color: '#0f172a' }}>
                      <input
                        type="checkbox"
                        checked={form.aTodasMisMaterias}
                        onChange={(e) => handleToggleAllMaterias(e.target.checked)}
                        disabled={isLoadingData || isLoadingMaterias || !canCreateAula || form.cursoIds.length === 0}
                      />
                      Todas mis materias
                    </label>
                    <small style={{ color: '#64748b' }}>
                      Se mostrarán únicamente materias de los cursos seleccionados en los que estás asignado.
                    </small>
                    {plannedPairs.length > 0 && (
                      <small style={{ color: '#1d4ed8', fontWeight: 600 }}>
                        La selección actual generará {plannedPairs.length} {plannedPairs.length === 1 ? 'aula' : 'aulas'}.
                      </small>
                    )}
                    {coursesWithMultipleMaterias.length > 0 && (
                      <small style={{ color: '#b45309', fontWeight: 600 }}>
                        En estos cursos se crearán varias aulas diferenciadas por materia: {coursesWithMultipleMaterias.join(', ')}.
                      </small>
                    )}
                  </div>
                </div>
                {hasAgentesClasicos && (
                  <div className="form-field">
                    <label>Asistentes IA del aula</label>
                    <SearchableSelect
                      value={form.idsAsistentes}
                      onChange={(value) => handleChange('idsAsistentes', value)}
                      options={assistantOptions}
                      placeholder="Busca asistentes IA por nombre, materia o área"
                      emptyText="No tienes asistentes IA finalizados disponibles."
                      noResultsText="No encontramos asistentes IA con esa búsqueda."
                      disabled={isLoadingData || !canCreateAula}
                      multiple
                    />
                    <small style={{ color: '#64748b' }}>
                      Aquí solo aparecen asistentes IA activos, finalizados y con URL pública. El aula mostrará únicamente los seleccionados aquí.
                    </small>
                  </div>
                )}

                {message && (
                  <div className={`form-message ${messageType}`} style={{ marginBottom: '1rem' }}>
                    {message}
                  </div>
                )}

                <div className="form-actions">
                  <button className="btn btn-secondary" type="button" onClick={() => navigate('/my-aulas')}>
                    Cancelar
                  </button>
                  <button className="btn btn-primary" type="submit" disabled={isSubmitting || !canCreateAula}>
                    {isSubmitting
                      ? 'Guardando...'
                      : plannedPairs.length > 1
                        ? `Crear ${plannedPairs.length} aulas`
                        : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </main>

      {(isLoadingData || isLoadingMaterias || isSubmitting) && (
        <LoadingSpinner
          message={
            isSubmitting
              ? 'Guardando aulas...'
              : isLoadingMaterias
                ? 'Obteniendo materias disponibles...'
                : 'Obteniendo datos...'
          }
        />
      )}
    </div>
  );
};

export default ProfesorCreateAula;
